import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
    Check, 
    X, 
    Calendar, 
    AlertCircle, 
    Wrench,
    Clock,
    UserMinus
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AppointmentApprovalService, ApprovalRequestType, ApprovalRequestData } from "@/services/appointment-approval.service";
import { toast } from "sonner";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "@/lib/store";
import { organization_type } from "@/models/organization.model";

interface ApprovalMessageProps {
    messageId: string;
    approvalData: ApprovalRequestData;
    className?: string;
    isProcessed?: boolean;
}

export default function ApprovalMessage({
    messageId,
    approvalData,
    className,
    isProcessed = false
}: ApprovalMessageProps) {
    const { roomId } = useParams();
    const [isProcessing, setIsProcessing] = useState(false);  
    const [isResolved, setIsResolved] = useState(isProcessed);
    
    // Get current user's organization type to determine if they should see approval buttons
    const activeOrg = useSelector((s: RootState) => {
        const activeOrgId = s.organization.activeOrgId;
        return s.organization.organizations.find(org => org.id === activeOrgId);
    });
    
    const currentUser = useSelector((s: RootState) => s.auth.user);
    
    // Determine if current user should see approval buttons
    const shouldShowApprovalButtons = () => {
        if (!activeOrg || !currentUser) return false;
        
        // Don't show buttons if request is from the same user
        if (approvalData.requestedBy === currentUser.id) return false;
        
        const currentUserRole = activeOrg.type === organization_type.pmc ? "pmc" : "vendor";
        const requestedByRole = approvalData.requestedByRole;
        
        // Show buttons based on approval logic:
        // - If vendor requested, show to PMC
        // - If PMC requested, show to vendor
        // - If individual requested, show to vendor (for reassignment) or PMC (for service changes)
        if (requestedByRole === "individual") {
            if (approvalData.type === ApprovalRequestType.REASSIGNMENT) {
                return currentUserRole === "vendor";
            } else if (approvalData.type === ApprovalRequestType.SERVICE_CHANGE) {
                return currentUserRole === "pmc";
            }
        }
        
        return currentUserRole !== requestedByRole;
    };

    const getIcon = () => {
        switch (approvalData.type) {
            case ApprovalRequestType.RESCHEDULE:
                return Calendar;
            case ApprovalRequestType.CANCEL:
                return AlertCircle;
            case ApprovalRequestType.SERVICE_CHANGE:
                return Wrench;
            case ApprovalRequestType.REASSIGNMENT:
                return UserMinus;
            default:
                return Clock;
        }
    };

    const getRequestDetails = () => {
        switch (approvalData.type) {
            case ApprovalRequestType.RESCHEDULE: {
                const data = approvalData.data;
                return (
                    <div className="space-y-1">
                        <p className="text-sm">
                            <strong>From:</strong> {data.currentDate || 'Current date'}
                        </p>
                        <p className="text-sm">
                            <strong>To:</strong> {data.newDate}
                        </p>
                    </div>
                );
            }
            
            case ApprovalRequestType.SERVICE_CHANGE: {
                const data = approvalData.data;
                return (
                    <div className="space-y-1">
                        <p className="text-sm">
                            <strong>From:</strong> {data.currentServiceName}
                        </p>
                        <p className="text-sm">
                            <strong>To:</strong> {data.newServiceName}
                        </p>
                    </div>
                );
            }
            
            case ApprovalRequestType.CANCEL: {
                const data = approvalData.data;
                return data.reason ? (
                    <p className="text-sm">
                        <strong>Reason:</strong> {data.reason}
                    </p>
                ) : null;
            }
            
            case ApprovalRequestType.REASSIGNMENT: {
                const data = approvalData.data;
                return (
                    <div className="space-y-1">
                        <p className="text-sm">
                            <strong>Current Individual:</strong> {data.currentIndividualName || 'Unknown'}
                        </p>
                        {data.reason && (
                            <p className="text-sm">
                                <strong>Reason:</strong> {data.reason}
                            </p>
                        )}
                    </div>
                );
            }
            
            default:
                return null;
        }
    };

    const getActionLabel = () => {
        switch (approvalData.type) {
            case ApprovalRequestType.RESCHEDULE:
                return "Reschedule Request";
            case ApprovalRequestType.CANCEL:
                return "Cancellation Request";
            case ApprovalRequestType.SERVICE_CHANGE:
                return "Service Change Request";
            case ApprovalRequestType.REASSIGNMENT:
                return "Reassignment Request";
            default:
                return "Approval Request";
        }
    };

    const handleApprove = async () => {
        if (!roomId || isProcessing) return;

        setIsProcessing(true);
        try {
            await AppointmentApprovalService.approveRequest(roomId, messageId, approvalData);
            setIsResolved(true);
            toast.success("Request approved successfully");
        } catch (error: any) {
            toast.error(error.message || "Failed to approve request");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!roomId || isProcessing) return;

        setIsProcessing(true);
        try {
            await AppointmentApprovalService.rejectRequest(roomId, messageId);
            setIsResolved(true);
            toast.success("Request rejected");
        } catch (error: any) {
            toast.error(error.message || "Failed to reject request");
        } finally {
            setIsProcessing(false);
        }
    };

    const Icon = getIcon();

    // If processed, show minified version
    if (isResolved) {
        return (
            <div className={cn(
                "max-w-sm mx-auto my-2 border rounded-md p-2 bg-muted/30 text-xs text-muted-foreground",
                className
            )}>
                <div className="flex items-center gap-2">
                    <Icon className="w-3 h-3" />
                    <span>{getActionLabel()} from {approvalData.requestedByName} was processed</span>
                </div>
            </div>
        );
    }

    return (
        <div className={cn(
            "max-w-md mx-auto my-4 border rounded-lg p-4 bg-card",
            className
        )}>
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-blue-600" />
                    </div>
                </div>
                
                <div className="flex-1 space-y-3">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium text-sm">{getActionLabel()}</h4>
                            <Badge variant="outline" className="text-xs">
                                {approvalData.requestedByRole.toUpperCase()}
                            </Badge>
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-2">
                            {approvalData.requestedByName} has requested this change:
                        </p>
                        
                        {getRequestDetails()}
                    </div>
                    
                    {shouldShowApprovalButtons() && !isResolved && (
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                onClick={handleApprove}
                                disabled={isProcessing}
                                className="h-8 px-3"
                            >
                                {isProcessing ? (
                                    <Clock className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                    <Check className="w-3 h-3 mr-1" />
                                )}
                                Approve
                            </Button>
                            
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleReject}
                                disabled={isProcessing}
                                className="h-8 px-3"
                            >
                                <X className="w-3 h-3 mr-1" />
                                Reject
                            </Button>
                        </div>
                    )}
                    
                    {!shouldShowApprovalButtons() && !isResolved && (
                        <div className="text-xs text-muted-foreground">
                            Waiting for {
                                approvalData.requestedByRole === "individual"
                                    ? (approvalData.type === ApprovalRequestType.REASSIGNMENT ? "vendor" : "PMC")
                                    : (approvalData.requestedByRole === "pmc" ? "vendor" : "PMC")
                            } approval...
                        </div>
                    )}
                    
                    {isResolved && (
                        <div className="text-xs text-muted-foreground">
                            This request has been processed.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
